// src/pages/About.js
import { useNavigate } from "react-router-dom";

export default function About() {
  const navigate = useNavigate();

  const values = [
    { icon: "🛡️", title: "Safety First", desc: "Every delivery is insured. Your goods are protected from the moment we pick them up to the moment they're delivered." },
    { icon: "📞", title: "Always Reachable", desc: "We pick up. Always. No unanswered calls after pickup — our team is available Mon–Sat, 8am–8pm." },
    { icon: "📍", title: "Real-Time Visibility", desc: "Track your shipment live. No more guessing — you see exactly where your goods are at every stage." },
    { icon: "🤝", title: "Client-First Service", desc: "We're the warm, communicative, responsive alternative to cold corporate logistics. You're never just a number." },
  ];

  const team = [
    { name: "Dr Patrick Nnadozie Esq", role: "Founder & CEO", desc: "Visionary leader with deep expertise in logistics and business development across Nigeria's major corridors." },
    { name: "Operations Team", role: "Lagos Hub — Jibowu", desc: "Our Lagos team handles end-to-end coordination for same-day, next-day, and interstate shipments from Jibowu." },
    { name: "Operations Team", role: "Port Harcourt Hub — Waterlines", desc: "Our Port Harcourt team manages the PH side of Nigeria's busiest delivery corridor with precision and care." },
  ];

  const milestones = [
    { year: "2024", event: "Pulse Logistics founded in Lagos by Dr Patrick Nnadozie" },
    { year: "2024", event: "First hub opened in Jibowu, Lagos" },
    { year: "2025", event: "Port Harcourt hub launched at Waterlines" },
    { year: "2025", event: "Lagos ↔ Port Harcourt interstate corridor established" },
    { year: "2026", event: "Digital platform launched — online tracking and booking goes live" },
    { year: "2026", event: "Insurance policy introduced for all deliveries" },
  ];

  return (
    <div style={{ background: "#F7FAFB", minHeight: "100vh" }}>

      {/* ── Hero ── */}
      <section style={{ background: "linear-gradient(160deg,#0B1E3D 0%,#1A3460 60%,#0E3060 100%)", padding: "72px 24px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, opacity: .04, backgroundImage: "repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 56px),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 56px)" }} />
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 32, alignItems: "start" }}>
          <div>
            <span className="tag tag-dark" style={{ marginBottom: 18, display: "inline-block" }}>About Us</span>
            <h1 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "clamp(26px,3.5vw,44px)", lineHeight: 1.2, color: "#fff", marginBottom: 16 }}>
              The logistics company that{" "}
              <span style={{ backgroundImage: "linear-gradient(135deg,#00C6D7,#00A896)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                actually cares.
              </span>
            </h1>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,.55)", lineHeight: 1.8, maxWidth: 440 }}>
              We started Pulse Logistics because we were tired of watching Nigerian businesses lose customers to unreliable couriers. We knew we could do better — and we have.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              { value: "2", label: "City Hubs", icon: "📍" },
              { value: "100%", label: "Insured", icon: "🛡️" },
              { value: "98.3%", label: "On-Time", icon: "⚡" },
              { value: "1,200+", label: "Deliveries", icon: "📦" },
            ].map(s => (
              <div key={s.label} style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, padding: "16px", textAlign: "center" }}>
                <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 20, backgroundImage: "linear-gradient(135deg,#00C6D7,#00A896)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Story ── */}
      <section style={{ background: "#fff", padding: "64px 24px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 32, alignItems: "start" }}>
            <div>
              <span className="tag tag-cyan" style={{ marginBottom: 14, display: "inline-block" }}>Our Story</span>
              <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 26, color: "#0B1E3D", marginBottom: 18 }}>
                Born from frustration. Built with purpose.
              </h2>
              <p style={{ color: "#374151", fontSize: 14, lineHeight: 1.85, marginBottom: 14 }}>
                Pulse Logistics Company Ltd was founded by Dr Patrick Nnadozie with a single, powerful conviction: Nigerian businesses deserve a logistics partner they can actually trust.
              </p>
              <p style={{ color: "#374151", fontSize: 14, lineHeight: 1.85, marginBottom: 14 }}>
                Too many logistics companies in Nigeria were leaving customers in the dark — unanswered calls after pickup, no tracking, damaged goods with no recourse, and rude service. We built Pulse to be the opposite of all of that.
              </p>
              <p style={{ color: "#374151", fontSize: 14, lineHeight: 1.85, marginBottom: 24 }}>
                Today, we operate dual hubs in <strong>Lagos (Jibowu)</strong> and <strong>Port Harcourt (Waterlines)</strong>, handling everything from single parcels to bulk interstate freight — with every delivery insured.
              </p>
              <div style={{ padding: "14px 18px", background: "#F7FAFB", borderRadius: 10, borderLeft: "3px solid #00C6D7" }}>
                <p style={{ color: "#0B1E3D", fontWeight: 600, fontSize: 14, fontStyle: "italic", margin: 0 }}>
                  "We dey feel your pulse, we dey move your goods."
                </p>
                <p style={{ color: "#6B7280", fontSize: 12, margin: "4px 0 0" }}>— Dr Patrick Nnadozie, Founder & CEO</p>
              </div>
            </div>
            <div style={{ background: "linear-gradient(135deg,#0B1E3D,#1A3460)", borderRadius: 14, padding: "32px 26px" }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 16, color: "#fff", marginBottom: 22 }}>Our Promise to You</div>
              {[
                "Every delivery is insured — no exceptions",
                "Real-time tracking on every shipment",
                "We always pick up the phone",
                "Transparent pricing — no hidden fees",
                "Proof of delivery on every order",
                "Mon–Sat, 8am–8pm availability",
              ].map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", flexShrink: 0, marginTop: 2 }}>✓</div>
                  <span style={{ color: "rgba(255,255,255,.75)", fontSize: 13, lineHeight: 1.5 }}>{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section style={{ background: "#F7FAFB", padding: "64px 24px", borderTop: "1px solid #E2EBF0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <span className="tag tag-navy" style={{ marginBottom: 12, display: "inline-block" }}>Our Values</span>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 26, color: "#0B1E3D" }}>What we stand for</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 20 }}>
            {values.map(v => (
              <div key={v.title} className="card card-hover">
                <div style={{ fontSize: 32, marginBottom: 14 }}>{v.icon}</div>
                <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15, color: "#0B1E3D", marginBottom: 8 }}>{v.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.65 }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Milestones ── */}
      <section style={{ background: "#fff", padding: "64px 24px", borderTop: "1px solid #E2EBF0" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <span className="tag tag-cyan" style={{ marginBottom: 12, display: "inline-block" }}>Our Journey</span>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 26, color: "#0B1E3D" }}>How we got here</h2>
          </div>
          {milestones.map((m, i) => (
            <div key={i} style={{ display: "flex", gap: 20, paddingBottom: i < milestones.length - 1 ? 24 : 0 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 11, color: "#fff", flexShrink: 0 }}>{m.year}</div>
                {i < milestones.length - 1 && <div style={{ width: 2, flex: 1, marginTop: 6, background: "linear-gradient(#00A896,#E2EBF0)" }} />}
              </div>
              <div style={{ paddingTop: 10 }}>
                <p style={{ fontSize: 14, color: "#374151", fontWeight: 500, lineHeight: 1.6 }}>{m.event}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Team ── */}
      <section style={{ background: "#F7FAFB", padding: "64px 24px", borderTop: "1px solid #E2EBF0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <span className="tag tag-cyan" style={{ marginBottom: 12, display: "inline-block" }}>Our Team</span>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 26, color: "#0B1E3D" }}>The people behind Pulse</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20 }}>
            {team.map(t => (
              <div key={t.name} className="card">
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Syne',sans-serif", fontWeight: 800, color: "#fff", fontSize: 20, marginBottom: 14 }}>{t.name[0]}</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15, color: "#0B1E3D", marginBottom: 4 }}>{t.name}</div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#00A896", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>{t.role}</div>
                <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.65 }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hubs ── */}
      <section style={{ background: "#fff", padding: "64px 24px", borderTop: "1px solid #E2EBF0" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
          <span className="tag tag-navy" style={{ marginBottom: 12, display: "inline-block" }}>Our Hubs</span>
          <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 26, color: "#0B1E3D", marginBottom: 36 }}>Where we operate</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 20 }}>
            {[
              { city: "Lagos Hub", location: "Jibowu, Lagos", coverage: "Lagos Island, Mainland, Lekki, VI, Ajah, Ikeja, Surulere and surrounding areas." },
              { city: "Port Harcourt Hub", location: "Waterlines, Port Harcourt", coverage: "Trans Amadi, Mile 1–4, GRA, Rumuola, Rumuokoro, Eliozu and surrounding areas." },
            ].map(h => (
              <div key={h.city} style={{ padding: "24px 20px", background: "#F7FAFB", borderRadius: 12, border: "1px solid #E2EBF0", textAlign: "left" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, marginBottom: 14 }}>📍</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15, color: "#0B1E3D", marginBottom: 4 }}>{h.city}</div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#00A896", marginBottom: 10 }}>{h.location}</div>
                <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.65 }}>{h.coverage}</p>
              </div>
            ))}
          </div>
          <div style={{ padding: "14px 20px", background: "rgba(0,198,215,.05)", borderRadius: 10, border: "1px solid rgba(0,198,215,.2)", fontSize: 13, color: "#374151" }}>
            🚀 <strong>Interstate corridor:</strong> Lagos (Jibowu) ↔ Port Harcourt (Waterlines) — our flagship route with daily runs.
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: "64px 24px", background: "#F7FAFB" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{ background: "linear-gradient(135deg,#0B1E3D,#1A3460)", borderRadius: 14, padding: "44px 40px", textAlign: "center" }}>
            <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 24, color: "#fff", marginBottom: 10 }}>Ready to experience the difference?</h3>
            <p style={{ color: "rgba(255,255,255,.5)", fontSize: 14, marginBottom: 28 }}>Join hundreds of businesses who trust Pulse Logistics with their deliveries.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="btn btn-primary" onClick={() => navigate("/request")} style={{ fontSize: 14, padding: "12px 24px" }}>📦 Book a Pickup</button>
              <a href="https://wa.me/2348023753147" target="_blank" rel="noopener noreferrer" className="btn btn-wa" style={{ fontSize: 14, padding: "12px 24px" }}>💬 Chat on WhatsApp</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: "#0B1E3D", padding: "28px 24px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <span style={{ color: "rgba(255,255,255,.25)", fontSize: 12 }}>© 2026 Pulse Logistics Company Ltd · All rights reserved.</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "rgba(255,255,255,.2)", fontSize: 11 }}>Designed & Built by</span>
            <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 12, backgroundImage: "linear-gradient(135deg,#00C6D7,#00A896)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Comvara</span>
          </div>
        </div>
      </footer>
    </div>
  );
}